import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {getFeedRecords, createFeedRecord, updateFeedRecord, fetchPond} from "../../api/pond";
import {fetchFeeds, fetchTemplateNames, fetchAllTemplates} from "../../api/feed";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";
import {useTranslation} from "react-i18next";

interface FeedTabProps {
  pondId: string;
  cycleId?: string;
  isCycleActive?: boolean;
  cycleStartDate?: string;
}

const FeedTab: React.FC<FeedTabProps> = ({pondId, cycleId, isCycleActive = true, cycleStartDate}) => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const [feedRecords, setFeedRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [feeds, setFeeds] = useState<any[]>([]);
  const [templateNames, setTemplateNames] = useState<any[]>([]);
  const [allTemplates, setAllTemplates] = useState<any[]>([]);
  const [selectedFeed, setSelectedFeed] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [currentDOC, setCurrentDOC] = useState<number>(0);
  const [predictedFCR, setPredictedFCR] = useState<number | null>(null);
  const [pondDetails, setPondDetails] = useState<any>(null);

  const form = useForm({
    initialValues: {
      feedDate: "",
      feed1: 0,
      feed2: 0,
      feed3: 0,
      feed4: 0,
      remark: "",
      feedId: "",
    },
    validationConfig: FORM_VALIDATION_CONFIGS.FEED_RECORD,
    validationContext: {
      cycleStartDate: cycleStartDate,
    },
    onSubmit: async (values) => {
      try {
        // Check if there's a selected cycle before allowing feed record creation
        if (!cycleId) {
          setError("No cycle selected. Please select a cycle first.");
          return;
        }

        if (!isCycleActive) {
          setError("Cannot add feed records to a closed cycle.");
          return;
        }

        // Convert feed values to numbers and ensure they are valid
        const feedData = {
          ...values,
          feed1: Number(values.feed1) || 0,
          feed2: Number(values.feed2) || 0,
          feed3: Number(values.feed3) || 0,
          feed4: Number(values.feed4) || 0,
          // Use the pond's feed type automatically
          feedType: pondDetails?.feedType || "",
        };

        if (editingRecord) {
          await updateFeedRecord(editingRecord.id, feedData);
        } else {
          await createFeedRecord(pondId, feedData);
        }
        setModalOpen(false);
        setError(null);
        fetchFeedRecords();
      } catch (err: any) {
        console.error("Feed record save error:", err);
        setError(err.response?.data?.message || "Failed to save feed record");
        throw err;
      }
    },
    onError: (errors) => {
      console.log("Validation errors:", errors);
    },
  });

  useEffect(() => {
    fetchFeedRecords();
    loadTemplateNames();
    loadPondDetails();
    loadFeeds(); // Load feeds for all users, backend handles tenant filtering
  }, [pondId, cycleId, user?.role]);

  // Update validation context when cycleStartDate changes
  useEffect(() => {
    form.updateValidationContext({
      cycleStartDate: cycleStartDate,
    });
    // Force re-validation when cycle changes
    if (form.values.feedDate) {
      form.validateField("feedDate");
    }
  }, [cycleStartDate]);


  const fetchFeedRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFeedRecords(pondId, cycleId);
      setFeedRecords(data.data || data);
    } catch (err) {
      setError("Failed to load feed records");
    } finally {
      setLoading(false);
    }
  };

  const loadFeeds = async () => {
    try {
      console.log("Loading feeds...");
      const feedsData = await fetchFeeds();
      console.log("Feeds loaded:", feedsData);
      setFeeds(feedsData);
    } catch (err) {
      console.error("Failed to load feeds:", err);
      setFeeds([]); // Ensure feeds is set to empty array on error
    }
  };

  const loadTemplateNames = async () => {
    try {
      const templateNamesData = await fetchTemplateNames();
      const allTemplatesData = await fetchAllTemplates();
      setTemplateNames(templateNamesData);
      setAllTemplates(allTemplatesData);
    } catch (err) {
      console.error("Failed to load template names:", err);
    }
  };

  const loadPondDetails = async () => {
    try {
      const pondData = await fetchPond(pondId);
      setPondDetails(pondData);
      // Set the selected template to the pond's feed type
      if (pondData.feedType) {
        setSelectedTemplate(pondData.feedType);
      }
    } catch (err) {
      console.error("Failed to load pond details:", err);
    }
  };

  const handleOpenModal = (record?: any) => {
    if (record) {
      setEditingRecord(record);
      form.setValues({
        feedDate: record.feedDate ? new Date(record.feedDate).toISOString().split("T")[0] : "",
        feed1: record.feed1 || 0,
        feed2: record.feed2 || 0,
        feed3: record.feed3 || 0,
        feed4: record.feed4 || 0,
        remark: record.remark || "",
        feedId: record.feedId || "",
      });
    } else {
      setEditingRecord(null);
      form.setValues({
        feedDate: new Date().toISOString().split("T")[0],
        feed1: 0,
        feed2: 0,
        feed3: 0,
        feed4: 0,
        remark: "",
        feedId: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
    form.reset();
  };

  const calculateTotal = () => {
    const feed1 = Number(form.values.feed1) || 0;
    const feed2 = Number(form.values.feed2) || 0;
    const feed3 = Number(form.values.feed3) || 0;
    const feed4 = Number(form.values.feed4) || 0;
    return feed1 + feed2 + feed3 + feed4;
  };

  // Calculate predicted FCR based on selected template and DOC
  const calculatePredictedFCR = () => {
    if (selectedTemplate && currentDOC > 0) {
      const template = allTemplates.find((t) => t.templateName === selectedTemplate && t.doc === currentDOC);
      return template?.fcrPercentage || null;
    }
    return null;
  };

  // Update predicted FCR when template or DOC changes
  React.useEffect(() => {
    const predicted = calculatePredictedFCR();
    setPredictedFCR(predicted);
  }, [selectedTemplate, currentDOC, allTemplates]);

  // Helper to get date-only (ignoring time)
  function getDateOnly(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t("Feed Records")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenModal()}
          disabled={!cycleId || !isCycleActive}
          title={!cycleId ? "No cycle selected" : !isCycleActive ? "Cannot add records to closed cycles" : ""}
        >
          {t("Add Feed Record")}
        </Button>
      </Box>

      {!cycleId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("No cycle selected for this pond. Please select a cycle from the dropdown above.")}
        </Alert>
      )}
      {cycleId && !isCycleActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}


      {/* Feed Selection Dropdown */}
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          User: {user?.name} ({user?.role}) | Feeds: {feeds.length}
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>{t("Select Feed")}</InputLabel>
          <Select value={selectedFeed} onChange={(e) => setSelectedFeed(e.target.value)} label={t("Select Feed")}>
            <MenuItem value="">
              <em>{t("All Feeds")}</em>
            </MenuItem>
            {feeds.map((feed) => (
              <MenuItem key={feed.id} value={feed.id}>
                {feed.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper} sx={{overflowX: "auto", maxWidth: "100%"}}>
        <Table size="small" sx={{minWidth: 500}}>
          <TableHead>
            <TableRow>
              <TableCell align="center">{t("DOC")}</TableCell>
              <TableCell align="center">{t("Date")}</TableCell>
              <TableCell>{t("Feed 1 (kg)")}</TableCell>
              <TableCell>{t("Feed 2 (kg)")}</TableCell>
              <TableCell>{t("Feed 3 (kg)")}</TableCell>
              <TableCell>{t("Feed 4 (kg)")}</TableCell>
              <TableCell>{t("Total (kg)")}</TableCell>
              <TableCell>{t("Remark")}</TableCell>
              <TableCell>{t("Cumulative Feed")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedRecords.map((record, idx) => {
              // Calculate DOC for each feed record using date-only
              let doc = "-";
              if (cycleStartDate && record.feedDate) {
                const feedDate = getDateOnly(new Date(record.feedDate));
                const startDate = getDateOnly(new Date(cycleStartDate));
                const calculatedDoc = Math.floor((feedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                doc = calculatedDoc.toString();

                // Debug logging
                console.log("DOC calculation:", {
                  feedDate: record.feedDate,
                  cycleStartedAt: cycleStartDate,
                  feedDateOnly: feedDate,
                  startDateOnly: startDate,
                  doc: calculatedDoc,
                });
              } else {
                console.log("Missing data for DOC calculation:", {
                  hasCycleId: !!cycleId,
                  hasFeedDate: !!record.feedDate,
                  hasStartedAt: !!cycleStartDate,
                });
              }
              return (
                <TableRow key={record.id}>
                  <TableCell align="center">{doc}</TableCell>
                  <TableCell align="center">{record.feedDate ? new Date(record.feedDate).toLocaleDateString() : "-"}</TableCell>
                  <TableCell>{record.feed1 || 0}</TableCell>
                  <TableCell>{record.feed2 || 0}</TableCell>
                  <TableCell>{record.feed3 || 0}</TableCell>
                  <TableCell>{record.feed4 || 0}</TableCell>
                  <TableCell>{record.totalQuantity || 0}</TableCell>
                  <TableCell sx={{maxWidth: 150, wordWrap: "break-word"}}>{record.remark || ""}</TableCell>
                  <TableCell>
                    {record.cumulativeFeed
                      ? typeof record.cumulativeFeed === "number"
                        ? record.cumulativeFeed.toFixed(2)
                        : parseFloat(record.cumulativeFeed).toFixed(2)
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleOpenModal(record)} disabled={!isCycleActive}>
                      {t("Edit")}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRecord ? t("Edit Feed Record") : t("Add Feed Record")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormField
              label={t("Date")}
              name="feedDate"
              type="date"
              value={form.values.feedDate}
              onChange={form.handleChange("feedDate")}
              error={form.errors.feedDate}
            />

            {/* DOC Input for FCR Prediction */}
            <TextField
              label={t("Current DOC (Days of Culture)")}
              type="number"
              value={currentDOC}
              onChange={(e) => setCurrentDOC(Number(e.target.value))}
              InputProps={{inputProps: {min: 0}}}
              helperText={t("Enter current DOC for FCR prediction")}
            />

            {/* FCR Prediction Display */}
            {predictedFCR !== null && (
              <Alert severity="info">
                <Typography variant="subtitle2">
                  {t("Predicted FCR for {{template}} at DOC {{doc}}:", {template: selectedTemplate, doc: currentDOC})}{" "}
                  <strong>{predictedFCR.toFixed(3)}</strong>
                </Typography>
              </Alert>
            )}

            {/* Feed Selection */}
            <FormControl fullWidth>
              <InputLabel>{t("Feed ID")}</InputLabel>
              <Select
                value={form.values.feedId}
                onChange={(e) => form.setValues({...form.values, feedId: e.target.value})}
                label={t("Feed ID")}
                error={!!form.errors.feedId}>
                {feeds.length === 0 && (
                  <MenuItem disabled>
                    <em>No feeds available</em>
                  </MenuItem>
                )}
                {feeds.map((feed) => (
                  <MenuItem key={feed.id} value={feed.id}>
                    {feed.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormField
              label={t("Feed 1 (kg)")}
              name="feed1"
              type="number"
              value={form.values.feed1}
              onChange={form.handleChange("feed1")}
              error={form.errors.feed1}
              min={0}
              step={0.01}
            />
            <FormField
              label={t("Feed 2 (kg)")}
              name="feed2"
              type="number"
              value={form.values.feed2}
              onChange={form.handleChange("feed2")}
              error={form.errors.feed2}
              min={0}
              step={0.01}
            />
            <FormField
              label={t("Feed 3 (kg)")}
              name="feed3"
              type="number"
              value={form.values.feed3}
              onChange={form.handleChange("feed3")}
              error={form.errors.feed3}
              min={0}
              step={0.01}
            />
            <FormField
              label={t("Feed 4 (kg)")}
              name="feed4"
              type="number"
              value={form.values.feed4}
              onChange={form.handleChange("feed4")}
              error={form.errors.feed4}
              min={0}
              step={0.01}
            />
            <TextField
              label={t("Total Quantity (kg)")}
              type="number"
              value={calculateTotal().toFixed(2)}
              InputProps={{readOnly: true}}
              sx={{backgroundColor: "#f5f5f5"}}
            />
            <FormField
              label={t("Remark")}
              name="remark"
              type="textarea"
              multiline
              rows={3}
              value={form.values.remark}
              onChange={form.handleChange("remark")}
              error={form.errors.remark}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
          <Button onClick={form.handleSubmit} variant="contained" disabled={form.isSubmitting}>
            {form.isSubmitting ? <CircularProgress size={20} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedTab;
